<script setup lang="ts">
import { computed, ref } from 'vue'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { VLoading, VModal } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { type Check, type Status } from '../types'
import { useCheckStore } from '../stores/check-store'
import CheckCard from './CheckCard.vue'
import CheckForm from './CheckForm.vue'
import SearchResults from './SearchResults.vue'
import StatusBadge from './StatusBadge.vue'
import SearchParams from './SearchParams.vue'

const authStore = useAuthStore()

const props = defineProps<{
  checkId: number | string
  status: Status
}>()

const isLoading = ref(false)
const modal = useModal()
const store = useCheckStore()
const check = ref<Check>({} as Check)

const canReviewCheck = computed(() => {
  return store.isReviewable({ status: props.status } as Check) && authStore.is_site_or_group_admin
})

const fetchAndOpenModal = async () => {
  if (!canReviewCheck.value) {
    return
  }

  isLoading.value = true
  check.value = await store.fetchCheck(props.checkId)
  isLoading.value = false

  if (store.isReviewable(check.value)) {
    modal.open()
  }
}
</script>

<template>
  <button
    @click="fetchAndOpenModal"
    class="relative"
    :class="canReviewCheck ? 'cursor-pointer' : 'cursor-default'"
    :disabled="isLoading"
  >
    <div :class="isLoading ? 'opacity-50' : ''">
      <StatusBadge :status="check?.status || status" />
    </div>

    <template v-if="isLoading">
      <div class="absolute inset-0 flex items-center justify-center">
        <VLoading />
      </div>
    </template>
  </button>
  <VModal :modalStore="modal" :has_footer="false">
    <template #main>
      <div class="flex flex-col gap-6">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-medium">AML Screening Check</h2>
          <StatusBadge :status="check?.status" />
        </div>
        <CheckCard :check="check" v-if="store.isReviewed(check)" />
        <CheckForm v-model="check" v-if="!store.isError(check || { status })" />
        <SearchParams :searchParams="store.searchParams(check)" />
        <SearchResults :searchResults="store.searchResults(check)" v-if="!store.isError(check || { status })" />
      </div>
    </template>
  </VModal>
</template>
